import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API } from '../services/api';
import TopBar from '../components/TopBar';
import './TemplateVersionsPage.css';

function TemplateVersionsPage() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [versions, setVersions] = useState([]);
    const [templateName, setTemplateName] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
    const [selectedVersion, setSelectedVersion] = useState(null);
    const [restoring, setRestoring] = useState(false);
    const [cascadeResults, setCascadeResults] = useState(null);

    useEffect(() => {
        loadVersions();
        loadTemplate();
    }, [id]);

    const loadTemplate = async () => {
        try {
            const response = await API.getTemplate(id);
            if (response && response.data) {
                setTemplateName(response.data.template.name);
            }
        } catch (error) {
            console.error('Error loading template:', error);
        }
    };

    const loadVersions = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await API.getTemplateVersions(id);
            if (response && response.data) {
                setVersions(response.data.versions);
            }
        } catch (error) {
            setError('Erreur lors du chargement des versions');
            console.error('Error loading versions:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleRestoreClick = (version) => {
        setSelectedVersion(version);
        setShowRestoreConfirm(true);
        setCascadeResults(null);
    };

    const handleConfirmRestore = async () => {
        if (!selectedVersion) return;

        try {
            setRestoring(true);
            const response = await API.restoreTemplateVersion(id, selectedVersion.id);

            if (response && response.data) {
                // Afficher les résultats du cascade update
                if (response.data.cascade_update) {
                    setCascadeResults(response.data.cascade_update);
                }

                // Recharger les versions
                await loadVersions();

                // Si pas de cascade ou cascade terminé, fermer le modal après 2 secondes
                if (!response.data.cascade_update || response.data.cascade_update.updated === 0) {
                    setTimeout(() => {
                        setShowRestoreConfirm(false);
                        setSelectedVersion(null);
                    }, 2000);
                }
            }
        } catch (error) {
            setError('Erreur lors de la restauration: ' + error.message);
            console.error('Error restoring version:', error);
        } finally {
            setRestoring(false);
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('fr-FR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="template-versions-page">
            <TopBar currentPage="templates" />

            <div className="versions-container">
                <div className="versions-header">
                    <button className="back-button" onClick={() => navigate('/templates')}>
                        ← Retour aux modèles
                    </button>
                    <h1>Versions du modèle : {templateName}</h1>
                </div>

                {loading && <div className="loading-state">Chargement des versions...</div>}

                {error && <div className="error-state">{error}</div>}

                {!loading && !error && versions.length === 0 && (
                    <div className="empty-state">
                        Aucune version disponible
                    </div>
                )}

                {!loading && !error && versions.length > 0 && (
                    <div className="versions-list">
                        {versions.map(version => (
                            <div key={version.id} className="version-card">
                                <div className="version-header">
                                    <div className="version-number">
                                        Version {version.version_number}
                                        {version.is_auto_save && <span className="auto-badge">Auto</span>}
                                    </div>
                                    <div className="version-date">{formatDate(version.created_at)}</div>
                                </div>

                                <div className="version-info">
                                    <div className="version-author">Par {version.created_by}</div>
                                    {version.comment && (
                                        <div className="version-comment">{version.comment}</div>
                                    )}
                                </div>

                                <div className="version-actions">
                                    <button
                                        className="btn-restore"
                                        onClick={() => handleRestoreClick(version)}
                                    >
                                        Restaurer cette version
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Modal de confirmation de restauration */}
            {showRestoreConfirm && (
                <div className="modal-overlay" onClick={() => !restoring && setShowRestoreConfirm(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>Confirmer la restauration</h2>

                        {!cascadeResults && (
                            <>
                                <p>
                                    Voulez-vous restaurer la <strong>version {selectedVersion?.version_number}</strong> ?
                                </p>
                                <p className="warning-text">
                                    ⚠️ Cette action va également mettre à jour toutes les signatures qui utilisent ce modèle.
                                </p>
                            </>
                        )}

                        {cascadeResults && (
                            <div className="cascade-results">
                                <h3>✅ Restauration terminée</h3>
                                <p>
                                    <strong>{cascadeResults.updated}</strong> signature(s) mise(s) à jour
                                </p>
                                {cascadeResults.locked > 0 && (
                                    <p className="locked-info">
                                        🔒 {cascadeResults.locked} signature(s) verrouillée(s) (non modifiée(s))
                                    </p>
                                )}
                                {cascadeResults.failed > 0 && (
                                    <p className="error-info">
                                        ❌ {cascadeResults.failed} signature(s) en erreur
                                    </p>
                                )}
                            </div>
                        )}

                        <div className="modal-actions">
                            {!cascadeResults && (
                                <>
                                    <button
                                        className="btn btn-secondary"
                                        onClick={() => setShowRestoreConfirm(false)}
                                        disabled={restoring}
                                    >
                                        Annuler
                                    </button>
                                    <button
                                        className="btn btn-primary"
                                        onClick={handleConfirmRestore}
                                        disabled={restoring}
                                    >
                                        {restoring ? 'Restauration...' : 'Confirmer'}
                                    </button>
                                </>
                            )}
                            {cascadeResults && (
                                <button
                                    className="btn btn-primary"
                                    onClick={() => {
                                        setShowRestoreConfirm(false);
                                        setSelectedVersion(null);
                                        setCascadeResults(null);
                                    }}
                                >
                                    Fermer
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default TemplateVersionsPage;
